/**
 * AddJobModal - Modal form to manually add a job to the pipeline.
 * Supports two tabs:
 * - Manual Entry: fill in job details by hand
 * - Paste & Parse: paste a job description for AI-powered auto-fill
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/common';
import { useToast } from '@/components/common';
import { vacancyService } from '@/services/vacancy.service';

interface AddJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: AddJobFormData) => void;
}

export interface AddJobFormData {
  title: string;
  company: string;
  location: string;
  url: string;
  source: string;
  description: string;
  skills?: string[];  // from AI parsing, display-only
}

const SOURCES = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'hh', label: 'HeadHunter' },
  { value: 'ats', label: 'Company Website' },
  { value: 'other', label: 'Other' },
] as const;

type ModalTab = 'manual' | 'parse';

const INITIAL_FORM: AddJobFormData = {
  title: '',
  company: '',
  location: '',
  url: '',
  source: '',
  description: '',
};

export function AddJobModal({ isOpen, onClose, onAdd }: AddJobModalProps) {
  const toast = useToast();
  const [form, setForm] = useState<AddJobFormData>({ ...INITIAL_FORM });
  const [activeTab, setActiveTab] = useState<ModalTab>('manual');
  const [parseText, setParseText] = useState('');
  const [parseUrl, setParseUrl] = useState('');
  const [parseSource, setParseSource] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset all state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...INITIAL_FORM });
    setActiveTab('manual');
    setParseText('');
    setParseUrl('');
    setParseSource('');
    setIsParsing(false);
    setParseError(null);
    // Focus title input after animation
    const timer = setTimeout(() => titleRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const updateField = useCallback(
    (field: keyof AddJobFormData, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.title.trim() || !form.company.trim()) return;
      onAdd(form);
      onClose();
    },
    [form, onAdd, onClose]
  );

  const handleParse = useCallback(async () => {
    if (!parseText.trim()) return;

    setIsParsing(true);
    setParseError(null);

    try {
      const result = await vacancyService.parseVacancy({
        text: parseText.trim(),
        url: parseUrl || undefined,
        source: (parseSource as any) || undefined,
        saveToLibrary: false,
      });

      const { parsed } = result;

      // Validate critical fields before auto-fill
      if (!parsed.title?.trim() && !parsed.company?.trim()) {
        toast.warning('AI could not extract job details. Please fill in manually.');
      }

      // Flatten skills from nested requirements structure
      const allSkills = [
        ...(parsed.requirements?.skills?.required ?? []),
        ...(parsed.requirements?.skills?.preferred ?? []),
      ];

      // Auto-fill form with parsed data
      setForm({
        title: parsed.title || '',
        company: parsed.company || '',
        location: parsed.location || '',
        url: parseUrl || '',
        source: parseSource || '',
        description: parseText.trim(), // Use ORIGINAL text, not AI summary
        skills: allSkills,
      });

      // Switch to manual tab for review
      setActiveTab('manual');
      toast.success('Parsed! Review and edit before adding.');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to parse';
      setParseError(msg);
      toast.error(msg);
    } finally {
      setIsParsing(false);
    }
  }, [parseText, parseUrl, parseSource, toast]);

  const isValid = form.title.trim().length > 0 && form.company.trim().length > 0;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="add-job-modal__backdrop" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div
        className="add-job-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-job-modal-title"
      >
        <div className="add-job-modal__content">
          {/* Header */}
          <div className="add-job-modal__header">
            <h2 id="add-job-modal-title" className="add-job-modal__title">
              Add Job to Pipeline
            </h2>
            <button
              className="add-job-modal__close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="add-job-modal__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'manual'}
              className={`add-job-modal__tab${activeTab === 'manual' ? ' add-job-modal__tab--active' : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              Manual Entry
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'parse'}
              className={`add-job-modal__tab${activeTab === 'parse' ? ' add-job-modal__tab--active' : ''}`}
              onClick={() => setActiveTab('parse')}
            >
              ✨ Paste &amp; Parse
            </button>
          </div>

          {activeTab === 'manual' ? (
            /* Manual Entry Form */
            <form className="add-job-modal__form" onSubmit={handleSubmit}>
              {/* Job Title */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label add-job-modal__label--required" htmlFor="add-job-title">
                  Job Title
                </label>
                <input
                  ref={titleRef}
                  id="add-job-title"
                  className="add-job-modal__input"
                  type="text"
                  placeholder="e.g. Senior Frontend Developer"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                />
              </div>

              {/* Company */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label add-job-modal__label--required" htmlFor="add-job-company">
                  Company
                </label>
                <input
                  id="add-job-company"
                  className="add-job-modal__input"
                  type="text"
                  placeholder="e.g. Google"
                  value={form.company}
                  onChange={(e) => updateField('company', e.target.value)}
                />
              </div>

              {/* Location + Source (side by side) */}
              <div className="add-job-modal__row">
                <div className="add-job-modal__field">
                  <label className="add-job-modal__label" htmlFor="add-job-location">
                    Location
                  </label>
                  <input
                    id="add-job-location"
                    className="add-job-modal__input"
                    type="text"
                    placeholder="e.g. Remote, Berlin"
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                  />
                </div>
                <div className="add-job-modal__field">
                  <label className="add-job-modal__label" htmlFor="add-job-source">
                    Source
                  </label>
                  <select
                    id="add-job-source"
                    className="add-job-modal__select"
                    value={form.source}
                    onChange={(e) => updateField('source', e.target.value)}
                  >
                    <option value="">Select source</option>
                    {SOURCES.map((src) => (
                      <option key={src.value} value={src.value}>
                        {src.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* URL */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label" htmlFor="add-job-url">
                  Job URL
                </label>
                <input
                  id="add-job-url"
                  className="add-job-modal__input"
                  type="url"
                  placeholder="https://..."
                  value={form.url}
                  onChange={(e) => updateField('url', e.target.value)}
                />
              </div>

              {/* Job Description */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label" htmlFor="add-job-description">
                  Job Description
                </label>
                <textarea
                  id="add-job-description"
                  className="add-job-modal__textarea"
                  placeholder="Paste the full job description here to enable ATS matching and tailored CV generation"
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={6}
                />
                {!form.description.trim() && (
                  <p className="add-job-modal__hint">
                    Adding a job description enables ATS score matching and tailored CV generation
                  </p>
                )}
              </div>

              {/* Skills chips (from AI parsing, read-only) */}
              {form.skills && form.skills.length > 0 && (
                <div className="add-job-modal__field">
                  <label className="add-job-modal__label">
                    Skills Found ({form.skills.length})
                  </label>
                  <div className="add-job-modal__skills">
                    {form.skills.map((skill, i) => (
                      <span key={i} className="add-job-modal__skill-chip">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="add-job-modal__actions">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={!isValid}>
                  Add to Pipeline
                </Button>
              </div>
            </form>
          ) : (
            /* Paste & Parse Tab */
            <div className="add-job-modal__parse">
              {/* Job description textarea */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label add-job-modal__label--required" htmlFor="parse-text">
                  Job Description
                </label>
                <textarea
                  id="parse-text"
                  className="add-job-modal__textarea"
                  placeholder="Paste the full job description here..."
                  value={parseText}
                  onChange={(e) => setParseText(e.target.value)}
                  rows={8}
                  disabled={isParsing}
                />
              </div>

              {/* Optional URL */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label" htmlFor="parse-url">
                  Job URL (optional)
                </label>
                <input
                  id="parse-url"
                  className="add-job-modal__input"
                  type="url"
                  placeholder="https://..."
                  value={parseUrl}
                  onChange={(e) => setParseUrl(e.target.value)}
                  disabled={isParsing}
                />
              </div>

              {/* Source select */}
              <div className="add-job-modal__field">
                <label className="add-job-modal__label" htmlFor="parse-source">
                  Source
                </label>
                <select
                  id="parse-source"
                  className="add-job-modal__select"
                  value={parseSource}
                  onChange={(e) => setParseSource(e.target.value)}
                  disabled={isParsing}
                >
                  <option value="">Select source</option>
                  {SOURCES.map((src) => (
                    <option key={src.value} value={src.value}>
                      {src.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Error alert */}
              {parseError && (
                <div className="add-job-modal__parse-error" role="alert">
                  {parseError}
                </div>
              )}

              {/* Actions */}
              <div className="add-job-modal__actions">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleParse}
                  disabled={!parseText.trim() || isParsing}
                  isLoading={isParsing}
                >
                  Parse with AI
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
